import { unwrap } from "@sagot/api-client";
import { DEFAULT_WEBHOOK_FORMAT, WEBHOOK_EVENTS, WEBHOOK_FORMATS, type WebhookDeliveryMode, type WebhookEventKey, type WebhookFormatKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Input, Switch, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import type { WebhookOut } from "../../shared";
import { formatAcceptsUrl, suggestWebhookFormat } from "../settings-model";
import type { EventDelivery } from "./event-delivery-notice";
import { SecretOnce } from "./panel-kit";
import { WebhookDeliveryField, deliveryModeOf, filtersFrom, type WebhookFiltersValue } from "./webhook-delivery-field";
import { WebhookFormatField } from "./webhook-format-field";

const HTTPS_URL = /^https:\/\/[^\s/$.?#][^\s]*$/i;

export function isWebhookUrl(value: string): boolean {
  return HTTPS_URL.test(value.trim());
}

const knownEvents = (events: readonly string[]): WebhookEventKey[] => WEBHOOK_EVENTS.filter((event) => events.includes(event));
const knownFormat = (format: string): WebhookFormatKey => WEBHOOK_FORMATS.find((key) => key === format) ?? DEFAULT_WEBHOOK_FORMAT;

interface WebhookDialogProps {
  webhook: WebhookOut | null;
  /** The Developer webhooks workflow, which sends the events (the endpoints report it; none before the workspace has it). */
  eventDelivery?: EventDelivery;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}

/** Add / edit an endpoint (https URL, message format, events). A new endpoint shows its signing secret once. */
export function WebhookDialog({ webhook, eventDelivery = null, onClose, onSaved }: WebhookDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [url, setUrl] = useState(webhook?.url ?? "");
  const [events, setEvents] = useState<WebhookEventKey[]>(webhook ? knownEvents(webhook.events) : ["conversation.created", "conversation.handoff"]);
  const [format, setFormat] = useState<WebhookFormatKey>(webhook ? knownFormat(webhook.format) : DEFAULT_WEBHOOK_FORMAT);
  const [formatChosen, setFormatChosen] = useState(false);
  const [enabled, setEnabled] = useState(webhook?.enabled ?? true);
  const [mode, setMode] = useState<WebhookDeliveryMode>(deliveryModeOf(webhook?.delivery_mode));
  const [filters, setFilters] = useState<WebhookFiltersValue>(filtersFrom(webhook?.filters));
  const [urlError, setUrlError] = useState<string | undefined>();
  const [formatError, setFormatError] = useState<string | undefined>();
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Until the person picks a format themselves, a Discord / Slack URL switches to its chat format (and back). */
  const changeUrl = (value: string) => {
    setUrl(value);
    setUrlError(undefined);
    setFormatError(undefined);
    if (!formatChosen) setFormat(suggestWebhookFormat(value) ?? DEFAULT_WEBHOOK_FORMAT);
  };
  const chooseFormat = (value: WebhookFormatKey) => {
    setFormat(value);
    setFormatChosen(true);
    setFormatError(undefined);
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!isWebhookUrl(url)) {
      setUrlError(t("settings.webhooks.urlInvalid"));
      return;
    }
    if (format !== "sagot" && !formatAcceptsUrl(format, url)) {
      setFormatError(t(`settings.webhooks.formatMismatch.${format}`));
      return;
    }
    setBusy(true);
    const body = { url: url.trim(), format, events: mode === "workflows" ? [] : events, enabled, delivery_mode: mode, filters };
    try {
      if (webhook) {
        await unwrap(api.PATCH("/v1/webhooks/{webhook_id}", { params: { path: { webhook_id: webhook.id } }, body }));
        toast.success(t("settings.webhooks.saved"));
        await onSaved();
        onClose();
      } else {
        const created = await unwrap(api.POST("/v1/webhooks", { body }));
        toast.success(t("settings.webhooks.saved"));
        await onSaved();
        setSecret(created.signing_secret);
      }
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };

  if (secret) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent title={t("settings.webhooks.secretTitle")} closeLabel={t("common.close")} footer={<Button onClick={onClose}>{t("settings.api.saved")}</Button>}>
          <SecretOnce value={secret} label={t("settings.webhooks.secretLabel")} warning={t("settings.webhooks.secretWarning")} />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t(webhook ? "settings.webhooks.editTitle" : "settings.webhooks.addTitle")} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
          <Field label={t("settings.webhooks.url")} error={urlError}>
            <Input type="url" className="font-mono text-xs" placeholder={t("settings.webhooks.urlPlaceholder")} value={url} onChange={(event) => { changeUrl(event.target.value); }} />
          </Field>
          <WebhookFormatField url={url} value={format} error={formatError} onChange={chooseFormat} />
          <WebhookDeliveryField mode={mode} filters={filters} events={events} eventDelivery={eventDelivery} onModeChange={setMode} onFiltersChange={setFilters} onEventsChange={setEvents} />
          <div className="flex items-center justify-between gap-4 rounded-md bg-surface-2 px-3 py-2.5">
            <span id="hook-enabled" className="text-sm font-semibold">
              {t("settings.webhooks.enabled")}
            </span>
            <Switch checked={enabled} onCheckedChange={setEnabled} aria-labelledby="hook-enabled" />
          </div>
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy} disabled={mode !== "workflows" && events.length === 0}>
              {t("settings.webhooks.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Rotate an endpoint's signing secret; the new one is shown once, the old one works for 24 h. */
export function RotateSecretDialog({ webhook, onClose }: { webhook: WebhookOut; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rotate = async () => {
    setBusy(true);
    try {
      const result = await unwrap(api.POST("/v1/webhooks/{webhook_id}/rotate-secret", { params: { path: { webhook_id: webhook.id } } }));
      setSecret(result.signing_secret);
      toast.warning(t("settings.webhooks.rotated"));
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("settings.webhooks.rotateTitle")}
        description={secret ? undefined : t("settings.webhooks.rotateBody")}
        closeLabel={t("common.close")}
        footer={
          secret ? (
            <Button onClick={onClose}>{t("settings.api.saved")}</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button loading={busy} onClick={() => void rotate()}>
                {t("settings.webhooks.rotate")}
              </Button>
            </>
          )
        }
      >
        {secret ? <SecretOnce value={secret} label={t("settings.webhooks.secretLabel")} warning={t("settings.webhooks.secretWarning")} /> : <p className="font-mono text-xs text-fg-muted">{webhook.url}</p>}
      </DialogContent>
    </Dialog>
  );
}
